/**
 * Video Management Page
 * Comprehensive page for managing brand showcase videos
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Row,
  Col,
  Button,
  Progress,
  Modal,
  Input,
  message,
  Spin,
  Empty,
  Tag,
  Typography,
  Space,
  Tooltip,
  Alert,
  Switch,
  Checkbox,
  Tabs,
  Badge,
  Steps,
  Table,
  Image,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
  PlusOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  RobotOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  OnboardingService,
  VideoForReview,
  VideoReviewStats,
  AiModel,
  BrandCustomModel,
} from '../services/onboardingService';
import { ProductService } from '../services/productService';
import { useAuth } from '../contexts/AuthContext';
import { CustomModelCreator } from '../components/custom-models';
import './VideoManagement.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

interface BrandProduct {
  id: string;
  sku: string;
  title: string;
  images: string[];
  price: number;
  currency: string;
}

const VideoManagement: React.FC = () => {
  const { productCode } = useParams<{ productCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const brandId = user?.brandId;

  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState<VideoForReview[]>([]);
  const [stats, setStats] = useState<VideoReviewStats | null>(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);

  // New video generation state
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [aiModels, setAiModels] = useState<AiModel[]>([]);
  const [products, setProducts] = useState<BrandProduct[]>([]);
  const [selectedAiModel, setSelectedAiModel] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Filter state
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Custom model state
  const [customModels, setCustomModels] = useState<BrandCustomModel[]>([]);
  const [customModelCount, setCustomModelCount] = useState<{ count: number; limit: number; remaining: number }>({ count: 0, limit: 10, remaining: 10 });
  const [createModelVisible, setCreateModelVisible] = useState(false);
  const [selectedModelType, setSelectedModelType] = useState<'system' | 'custom' | null>(null);

  const loadVideos = useCallback(async (options?: { showSpinner?: boolean }) => {
    if (!brandId || !productCode) return;

    try {
      if (options?.showSpinner) {
        setLoading(true);
      }
      const data = await OnboardingService.getVideosForReview(brandId, productCode);
      setVideos(data.videos);
      setStats(data.stats);
    } catch (error) {
      message.error('Videolar yüklenemedi');
    } finally {
      if (options?.showSpinner) {
        setLoading(false);
      }
    }
  }, [brandId, productCode]);

  useEffect(() => {
    loadVideos({ showSpinner: true });
  }, [loadVideos]);

  useEffect(() => {
    // Auto-refresh when videos are being generated
    if (stats && stats.pending > 0) {
      const interval = setInterval(() => loadVideos(), 5000);
      return () => clearInterval(interval);
    }
  }, [stats, loadVideos]);

  const loadAiModels = async () => {
    if (!brandId) return;
    
    try {
      setLoadingModels(true);
      const [models, customModelData] = await Promise.all([
        OnboardingService.getAiModels(),
        OnboardingService.getBrandCustomModels(brandId),
      ]);
      setAiModels(models);
      setCustomModels(customModelData.models);
      setCustomModelCount({
        count: customModelData.count,
        limit: customModelData.limit,
        remaining: customModelData.limit - customModelData.count,
      });
    } catch (error) {
      message.error('AI modeller yüklenemedi');
    } finally {
      setLoadingModels(false);
    }
  };

  const loadProducts = async () => {
    if (!brandId) return;

    try {
      setLoadingProducts(true);
      const response = await ProductService.getProducts({ brandId, limit: 100 });
      setProducts(response.data.products.map((p) => ({
        id: p.id,
        sku: p.sku,
        title: p.name,
        images: p.images,
        price: p.price,
        currency: p.currency,
      })));
    } catch (error) {
      message.error('Ürünler yüklenemedi');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleOpenGenerateModal = () => {
    setGenerateModalVisible(true);
    setSelectedAiModel(null);
    setSelectedProducts([]);
    setWizardStep(0);
    loadAiModels();
    loadProducts();
  };

  const getSelectedModelInfo = () => {
    if (selectedModelType === 'custom') {
      return customModels.find(m => m.id === selectedAiModel);
    }
    return aiModels.find(m => m.id === selectedAiModel);
  };

  const handleSelectModel = (modelId: string, type: 'system' | 'custom') => {
    setSelectedAiModel(modelId);
    setSelectedModelType(type);
  };

  const handleCustomModelCreated = (model: BrandCustomModel) => {
    setCustomModels(prev => [...prev, model]);
    setCustomModelCount(prev => ({
      ...prev,
      count: prev.count + 1,
      remaining: prev.remaining - 1,
    }));
    handleSelectModel(model.id, 'custom');
  };



  const handleGenerate = async () => {
    if (!brandId || !productCode || !selectedAiModel || selectedProducts.length === 0) {
      message.warning('Lütfen AI model ve en az bir ürün seçin');
      return;
    }

    try {
      setGenerating(true);
      const result = await OnboardingService.generateVideos(
        brandId,
        productCode,
        selectedAiModel,
        selectedProducts
      );
      message.success(`${result.totalItems} video oluşturma başlatıldı`);
      setGenerateModalVisible(false);
      await loadVideos();
    } catch (error) {
      message.error('Video oluşturma başlatılamadı');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (videoId: string) => {
    if (!brandId || !productCode) return;

    try {
      setProcessing(videoId);
      await OnboardingService.approveVideo(brandId, productCode, videoId);
      message.success('Video onaylandı');
      await loadVideos();
    } catch (error) {
      message.error('Video onaylanamadı');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (videoId: string) => {
    setSelectedVideoId(videoId);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!brandId || !productCode || !selectedVideoId) return;

    try {
      setProcessing(selectedVideoId);
      await OnboardingService.rejectVideo(brandId, productCode, selectedVideoId, rejectReason);
      message.success('Video reddedildi ve yeniden oluşturuluyor');
      setRejectModalVisible(false);
      await loadVideos();
    } catch (error) {
      message.error('Video reddedilemedi');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveAll = async () => {
    if (!brandId || !productCode) return;

    try {
      setProcessing('all');
      const count = await OnboardingService.approveAllVideos(brandId, productCode);
      message.success(`${count} video onaylandı`);
      await loadVideos();
    } catch (error) {
      message.error('Videolar onaylanamadı');
    } finally {
      setProcessing(null);
    }
  };

  const handleToggleActive = async (videoId: string) => {
    if (!brandId || !productCode) return;

    try {
      setProcessing(videoId);
      const result = await OnboardingService.toggleVideoStatus(brandId, productCode, videoId);
      message.success(result.isActive ? 'Video aktif edildi' : 'Video pasif yapıldı');
      await loadVideos();
    } catch (error) {
      message.error('Durum değiştirilemedi');
    } finally {
      setProcessing(null);
    }
  };

  const handleRegenerate = async (videoId: string) => {
    if (!brandId || !productCode) return;

    try {
      setProcessing(videoId);
      await OnboardingService.regenerateVideo(brandId, productCode, videoId);
      message.success('Video yeniden oluşturuluyor');
      await loadVideos();
    } catch (error) {
      message.error('Video yeniden oluşturulamadı');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusTag = (video: VideoForReview) => {
    if (video.generationStatus === 'pending' || video.generationStatus === 'processing') {
      return <Tag color="blue">Oluşturuluyor...</Tag>;
    }
    if (video.generationStatus === 'failed') {
      return <Tag color="red">Başarısız</Tag>;
    }
    if (video.approvalStatus === 'approved') {
      return <Tag color="green">Onaylandı</Tag>;
    }
    if (video.approvalStatus === 'rejected') {
      return <Tag color="orange">Reddedildi</Tag>;
    }
    return <Tag color="purple">İnceleme Bekliyor</Tag>;
  };

  const getProductName = () => {
    switch (productCode) {
      case 'fashsnap':
        return 'FashSnap';
      case 'fashplay':
        return 'FashPlay';
      default:
        return productCode;
    }
  };

  const filteredVideos = videos.filter((video) => {
    if (activeFilter === 'active') return video.isActive;
    if (activeFilter === 'inactive') return !video.isActive;
    return true;
  });

  if (loading) {
    return (
      <Layout style={{ padding: '24px' }}>
        <Content>
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Videolar yükleniyor...</p>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="video-management-page">
      <Content style={{ padding: '24px' }}>
        <div className="page-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            style={{ marginBottom: 16 }}
          >
            Dashboard'a Dön
          </Button>

          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2}>{getProductName()} Video Yönetimi</Title>
              <Text type="secondary">
                Marka videolarınızı yönetin, yeni videolar oluşturun veya mevcut videoları düzenleyin.
              </Text>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenGenerateModal}
                size="large"
              >
                Yeni Video Oluştur
              </Button>
            </Col>
          </Row>
        </div>

        {stats && (
          <Card className="stats-card" style={{ marginTop: 24, marginBottom: 24 }}>
            <Row gutter={24} align="middle">
              <Col flex="auto">
                <Space size="large" wrap>
                  <div>
                    <Text type="secondary">Toplam</Text>
                    <Title level={4} style={{ margin: 0 }}>{stats.total}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Aktif</Text>
                    <Title level={4} style={{ margin: 0, color: '#52c41a' }}>{stats.active}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Pasif</Text>
                    <Title level={4} style={{ margin: 0, color: '#8c8c8c' }}>{stats.inactive}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Tamamlanan</Text>
                    <Title level={4} style={{ margin: 0, color: '#1890ff' }}>{stats.completed}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Onaylanan</Text>
                    <Title level={4} style={{ margin: 0, color: '#52c41a' }}>{stats.approved}</Title>
                  </div>
                  <div>
                    <Text type="secondary">İnceleme Bekleyen</Text>
                    <Title level={4} style={{ margin: 0, color: '#722ed1' }}>{stats.pendingReview}</Title>
                  </div>
                  <div>
                    <Text type="secondary">Oluşturuluyor</Text>
                    <Title level={4} style={{ margin: 0, color: '#1890ff' }}>{stats.pending}</Title>
                  </div>
                </Space>
              </Col>
              <Col>
                <Progress
                  type="circle"
                  percent={stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0}
                  format={() => `${stats.approved}/${stats.total}`}
                  size={80}
                />
              </Col>
              <Col>
                {stats.pendingReview > 0 && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={handleApproveAll}
                    loading={processing === 'all'}
                    size="large"
                  >
                    Tümünü Onayla ({stats.pendingReview})
                  </Button>
                )}
              </Col>
            </Row>
          </Card>
        )}

        {stats && stats.pending > 0 && (
          <Alert
            message="Videolar Oluşturuluyor"
            description={`${stats.pending} video hala oluşturuluyor. Sayfa otomatik olarak güncellenecektir.`}
            type="info"
            showIcon
            action={
              <Button size="small" icon={<ReloadOutlined />} onClick={() => loadVideos()}>
                Yenile
              </Button>
            }
            style={{ marginBottom: 24 }}
          />
        )}

        <Card style={{ marginBottom: 24 }}>
          <Space>
            <Text>Filtrele:</Text>
            <Tabs
              activeKey={activeFilter}
              onChange={(key) => setActiveFilter(key as typeof activeFilter)}
              items={[
                { key: 'all', label: <Badge count={videos.length} showZero>Tümü</Badge> },
                { key: 'active', label: <Badge count={stats?.active || 0} showZero color="green">Aktif</Badge> },
                { key: 'inactive', label: <Badge count={stats?.inactive || 0} showZero color="default">Pasif</Badge> },
              ]}
            />
          </Space>
        </Card>

        {filteredVideos.length === 0 ? (
          <Empty
            description={
              activeFilter === 'all'
                ? 'Henüz video oluşturulmamış'
                : `${activeFilter === 'active' ? 'Aktif' : 'Pasif'} video bulunamadı`
            }
          >
            {activeFilter === 'all' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenGenerateModal}>
                İlk Videonuzu Oluşturun
              </Button>
            )}
          </Empty>
        ) : (
          <Row gutter={[24, 24]}>
            {filteredVideos.map((video) => (
              <Col xs={24} sm={12} md={8} lg={6} key={video.id}>
                <Card
                  className={`video-card ${video.approvalStatus} ${!video.isActive ? 'inactive' : ''}`}
                  cover={
                    <div className="video-cover">
                      {!video.isActive && (
                        <div className="inactive-overlay">
                          <EyeInvisibleOutlined />
                          <span>Pasif</span>
                        </div>
                      )}
                      {video.generatedVideoUrl ? (
                        <div
                          className="video-thumbnail"
                          onClick={() => setPreviewVideo(video.generatedVideoUrl || null)}
                        >
                          <img
                            src={video.productImage || video.generatedImageUrl}
                            alt={video.productName}
                          />
                          <div className="play-overlay">
                            <PlayCircleOutlined />
                          </div>
                        </div>
                      ) : video.generatedImageUrl ? (
                        <img src={video.generatedImageUrl} alt={video.productName} />
                      ) : (
                        <div className="placeholder">
                          {video.generationStatus === 'failed' ? (
                            <CloseCircleOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
                          ) : (
                            <Spin />
                          )}
                        </div>
                      )}
                    </div>
                  }
                  actions={[
                    <Tooltip title={video.isActive ? 'Pasif Yap' : 'Aktif Yap'} key="toggle">
                      <Switch
                        checked={video.isActive}
                        onChange={() => handleToggleActive(video.id)}
                        loading={processing === video.id}
                        checkedChildren={<EyeOutlined />}
                        unCheckedChildren={<EyeInvisibleOutlined />}
                      />
                    </Tooltip>,
                    <Tooltip title="Yeniden Oluştur" key="regenerate">
                      <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={() => handleRegenerate(video.id)}
                        loading={processing === video.id}
                        disabled={video.generationStatus === 'pending' || video.generationStatus === 'processing'}
                      />
                    </Tooltip>,
                    ...(video.generationStatus === 'completed' && video.approvalStatus === 'pending_review'
                      ? [
                          <Tooltip title="Onayla" key="approve">
                            <Button
                              type="text"
                              icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                              onClick={() => handleApprove(video.id)}
                              loading={processing === video.id}
                            />
                          </Tooltip>,
                          <Tooltip title="Reddet" key="reject">
                            <Button
                              type="text"
                              icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                              onClick={() => handleRejectClick(video.id)}
                              loading={processing === video.id}
                            />
                          </Tooltip>,
                        ]
                      : []),
                  ]}
                >
                  <Card.Meta
                    title={
                      <Space>
                        <span style={{ fontSize: 14 }}>{video.productName}</span>
                        {getStatusTag(video)}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={4}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Model: {video.aiModelName}
                        </Text>
                        {video.regenerationCount > 0 && (
                          <Text type="warning" style={{ fontSize: 12 }}>
                            <ReloadOutlined /> {video.regenerationCount}x yeniden oluşturuldu
                          </Text>
                        )}
                        {video.generationError && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {video.generationError}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Generate Videos Modal - Wizard */}
        <Modal
          title="Yeni Video Oluştur"
          open={generateModalVisible}
          onCancel={() => setGenerateModalVisible(false)}
          footer={
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {wizardStep > 0 && (
                  <Button onClick={() => setWizardStep(wizardStep - 1)}>
                    Geri
                  </Button>
                )}
              </div>
              <Space>
                <Button onClick={() => setGenerateModalVisible(false)}>
                  İptal
                </Button>
                {wizardStep === 0 ? (
                  <Button
                    type="primary"
                    disabled={!selectedAiModel}
                    onClick={() => setWizardStep(1)}
                  >
                    İleri
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    loading={generating}
                    disabled={selectedProducts.length === 0}
                    onClick={handleGenerate}
                  >
                    Video Oluştur ({selectedProducts.length} ürün)
                  </Button>
                )}
              </Space>
            </div>
          }
          width={900}
        >
          <Steps
            current={wizardStep}
            items={[
              { title: 'Manken Seç', description: selectedAiModel ? getSelectedModelInfo()?.name : 'Seçilmedi' },
              { title: 'Ürün Seç', description: selectedProducts.length > 0 ? `${selectedProducts.length} ürün` : 'Seçilmedi' },
            ]}
            style={{ marginBottom: 32 }}
          />

          {/* Step 1: AI Model Selection */}
          {wizardStep === 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <Title level={5} style={{ margin: 0 }}>AI Manken Seçin</Title>
                  <Text type="secondary">
                    Ürünlerinizin üzerinde görünecek AI mankeni seçin.
                  </Text>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModelVisible(true)}
                >
                  Yeni Model Oluştur
                </Button>
              </div>
              
              {loadingModels ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  {/* Custom Models Section */}
                  {customModels.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                        <UserOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                        <Text strong>Markanıza Özel Modeller</Text>
                        <Badge count={`${customModelCount.count}/${customModelCount.limit}`} style={{ marginLeft: 8, backgroundColor: '#722ed1' }} />
                      </div>
                      <Row gutter={[16, 16]}>
                        {customModels.map((model) => (
                          <Col xs={12} sm={8} md={6} key={model.id}>
                            <Badge.Ribbon
                              text={model.creationType === 'ai_generated' ? 'AI' : 'Yüklendi'}
                              color={model.creationType === 'ai_generated' ? 'purple' : 'blue'}
                            >
                              <Card
                                hoverable
                                className={`model-card-wizard ${selectedAiModel === model.id && selectedModelType === 'custom' ? 'selected' : ''}`}
                                onClick={() => handleSelectModel(model.id, 'custom')}
                                style={{
                                  border: selectedAiModel === model.id && selectedModelType === 'custom' ? '2px solid #722ed1' : '1px solid #d9d9d9',
                                  borderRadius: 12,
                                  overflow: 'hidden',
                                }}
                                bodyStyle={{ padding: 12, textAlign: 'center' }}
                              >
                                <div style={{ 
                                  position: 'relative',
                                  paddingBottom: '150%',
                                  overflow: 'hidden',
                                  borderRadius: 8,
                                  marginBottom: 12,
                                  background: '#f5f5f5',
                                }}>
                                  <img
                                    src={model.imageUrl || model.thumbnailUrl}
                                    alt={model.name}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                      objectPosition: 'center',
                                    }}
                                  />
                                  {selectedAiModel === model.id && selectedModelType === 'custom' && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      background: '#722ed1',
                                      borderRadius: '50%',
                                      width: 24,
                                      height: 24,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}>
                                      <CheckOutlined style={{ color: 'white', fontSize: 14 }} />
                                    </div>
                                  )}
                                </div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>{model.name}</Text>
                                <Tag color={model.gender === 'female' ? 'magenta' : 'blue'}>
                                  {model.gender === 'female' ? 'Kadın' : 'Erkek'}
                                </Tag>
                              </Card>
                            </Badge.Ribbon>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}

                  {/* System Models Section */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                      <RobotOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                      <Text strong>Hazır Modeller</Text>
                    </div>
                    <Row gutter={[16, 16]}>
                      {aiModels.map((model) => (
                        <Col xs={12} sm={8} md={6} key={model.id}>
                          <Card
                            hoverable
                            className={`model-card-wizard ${selectedAiModel === model.id && selectedModelType === 'system' ? 'selected' : ''}`}
                            onClick={() => handleSelectModel(model.id, 'system')}
                            style={{
                              border: selectedAiModel === model.id && selectedModelType === 'system' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                              borderRadius: 12,
                              overflow: 'hidden',
                            }}
                            bodyStyle={{ padding: 12, textAlign: 'center' }}
                          >
                            <div style={{ 
                              position: 'relative',
                              paddingBottom: '150%',
                              overflow: 'hidden',
                              borderRadius: 8,
                              marginBottom: 12,
                              background: '#f5f5f5',
                            }}>
                              <img
                                src={model.imageUrl || model.thumbnailUrl}
                                alt={model.name}
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  objectPosition: 'center',
                                }}
                              />
                              {selectedAiModel === model.id && selectedModelType === 'system' && (
                                <div style={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  background: '#1890ff',
                                  borderRadius: '50%',
                                  width: 24,
                                  height: 24,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <CheckOutlined style={{ color: 'white', fontSize: 14 }} />
                                </div>
                              )}
                            </div>
                            <Text strong style={{ display: 'block', marginBottom: 4 }}>{model.name}</Text>
                            <Tag color={model.gender === 'female' ? 'magenta' : 'blue'}>
                              {model.gender === 'female' ? 'Kadın' : 'Erkek'}
                            </Tag>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Product Selection */}
          {wizardStep === 1 && (
            <div>
              <Title level={5}>Ürünleri Seçin</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Video oluşturulacak ürünleri seçin. Birden fazla ürün seçebilirsiniz.
              </Text>
              {loadingProducts ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin size="large" />
                </div>
              ) : products.length === 0 ? (
                <Empty description="Henüz ürün eklenmemiş" />
              ) : (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Checkbox
                      checked={selectedProducts.length === products.length}
                      indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length}
                      onChange={(e) =>
                        setSelectedProducts(e.target.checked ? products.map((p) => p.id) : [])
                      }
                    >
                      Tümünü Seç
                    </Checkbox>
                    <Text type="secondary">
                      {selectedProducts.length} / {products.length} ürün seçildi
                    </Text>
                  </div>
                  <Table
                    dataSource={products}
                    rowKey="id"
                    pagination={{ pageSize: 5, showSizeChanger: false }}
                    rowSelection={{
                      selectedRowKeys: selectedProducts,
                      onChange: (keys) => setSelectedProducts(keys as string[]),
                    }}
                    columns={[
                      {
                        title: 'Görsel',
                        dataIndex: 'images',
                        key: 'image',
                        width: 100,
                        render: (images: string[], record) => (
                          images?.[0] ? (
                            <Image
                              src={images[0]}
                              alt={record.title}
                              width={70}
                              height={90}
                              style={{ objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                              preview={{
                                mask: <EyeOutlined style={{ fontSize: 16 }} />,
                              }}
                            />
                          ) : (
                            <div style={{
                              width: 70,
                              height: 90,
                              background: '#f5f5f5',
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <Text type="secondary">-</Text>
                            </div>
                          )
                        ),
                      },
                      {
                        title: 'SKU',
                        dataIndex: 'sku',
                        key: 'sku',
                        width: 120,
                        render: (sku: string) => <Text code>{sku}</Text>,
                      },
                      {
                        title: 'Ürün Adı',
                        dataIndex: 'title',
                        key: 'title',
                        width: 120,
                        render: (title: string) => (
                            <Text>{title}</Text>
                        ),
                      },
                      {
                        title: 'Fiyat',
                        dataIndex: 'price',
                        key: 'price',
                        width: 120,
                        render: (price: number, record) => (
                          <Text strong>
                            {price?.toLocaleString('tr-TR')} {record.currency || 'TRY'}
                          </Text>
                        ),
                      },
                    ]}
                  />
                </>
              )}
            </div>
          )}
        </Modal>

        {/* Image Preview Modal */}
        <Modal
          open={!!previewImage}
          footer={null}
          onCancel={() => setPreviewImage(null)}
          width={600}
          centered
        >
          {previewImage && (
            <img src={previewImage} alt="Preview" style={{ width: '100%' }} />
          )}
        </Modal>

        {/* Reject Modal */}
        <Modal
          title="Videoyu Reddet"
          open={rejectModalVisible}
          onCancel={() => setRejectModalVisible(false)}
          onOk={handleRejectConfirm}
          okText="Reddet ve Yeniden Oluştur"
          okButtonProps={{ danger: true, loading: processing === selectedVideoId }}
          cancelText="İptal"
        >
          <p>Bu videoyu reddetmek istediğinize emin misiniz? Video yeniden oluşturulacaktır.</p>
          <TextArea
            placeholder="Reddetme sebebi (opsiyonel)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
        </Modal>

        {/* Video Preview Modal */}
        <Modal
          title="Video Önizleme"
          open={!!previewVideo}
          onCancel={() => setPreviewVideo(null)}
          footer={null}
          width={800}
          centered
        >
          {previewVideo && (
            <video
              src={previewVideo}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '70vh' }}
            />
          )}
        </Modal>

        {/* Custom Model Creator Modal */}
        {brandId && (
          <CustomModelCreator
            visible={createModelVisible}
            brandId={brandId}
            onClose={() => setCreateModelVisible(false)}
            onSuccess={handleCustomModelCreated}
            remainingSlots={customModelCount.remaining}
          />
        )}
      </Content>
    </Layout>
  );
};

export default VideoManagement;
